import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { t } from 'i18next';
import Auth from '../../models/Auth';
import { PORTAL_INSTANCE } from '../..';
import NewInquiry from '../../Pages/ServiceSupport/ContactUs/NewInquiry';
import NewInquiryComplete from '../../Pages/ServiceSupport/ContactUs/NewInquiryComplete';

const styles = {
    button: {
        position: 'fixed', bottom: '28px', left: '12px', zIndex: 9999,
        height: '36px', padding: '0 18px 0 14px', borderRadius: '22px',
        background: '#0052CC', color: '#fff', border: 'none',
        boxShadow: '0 4px 16px rgba(0, 82, 204, 0.45)', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: '8px',
        fontSize: '14px', fontWeight: '600', letterSpacing: '0.3px',
        whiteSpace: 'nowrap', transition: 'background 0.2s, transform 0.15s, box-shadow 0.2s',
    },
};

export const NCP_OPEN_INQUIRY_GLOBAL = '__NCP_OPEN_INQUIRY';

export function requestOpenNcpInquiry() {
    if (typeof window === 'undefined') return;
    const fn = window[NCP_OPEN_INQUIRY_GLOBAL];
    if (typeof fn === 'function') fn();
}

function isSignInPath(pathname = '') {
    if (pathname === '/login' || pathname === '/' || pathname === '/req-qr/login') return true;
    if (pathname.startsWith('/req-qr/create/login') || pathname.startsWith('/req-qr/edit/login')) return true;
    if (pathname.indexOf('/home-customer/login') !== -1) return true;
    return false;
}

function SupportButton({ floating = true }) {
    const [showForm, setShowForm] = useState(false);
    const [showComplete, setShowComplete] = useState(false);
    const [item, setItem] = useState({});
    const navigate = useNavigate();
    const location = useLocation();
    const isGuest = useMemo(() => isSignInPath(location.pathname), [location.pathname]);
    const canCreateInquiry = Auth.hasRightAdd('CONTACT_US');
    const canUseSupport = isGuest || canCreateInquiry;

    const openInquiry = useCallback(() => {
        if (!isGuest && !Auth.hasRightAdd('CONTACT_US')) {
            toast.warn(`Your don't have permission to do this action, please contact administrator for more details`);
            return;
        }
        if (isGuest && (!PORTAL_INSTANCE.pId)) {
            toast.warn('Portal information is missing. Please refresh the page and try again.');
            return;
        }
        setItem({ id: 0, category: '', requestId: '' });
        setShowForm(true);
    }, [isGuest]);

    useEffect(() => {
        window[NCP_OPEN_INQUIRY_GLOBAL] = openInquiry;
        return () => {
            if (window[NCP_OPEN_INQUIRY_GLOBAL] === openInquiry) {
                delete window[NCP_OPEN_INQUIRY_GLOBAL];
            }
        };
    }, [openInquiry]);

    const onSaveInquiry = (savedItem) => {
        setShowForm(false);
        setShowComplete(true);
        setItem(savedItem);
        toast.success(t('NewInquiryCreated'));
    };

    const onViewDetail = (savedItem) => {
        setShowComplete(false);
        if (isGuest) return;
        navigate(`/dashboard/support/inquiry-detail/${savedItem.id}${location.search}`);
    };

    if (!canUseSupport) {
        return null;
    }

    return (
        <>
            {floating !== false && (
                <button
                    style={styles.button}
                    onClick={openInquiry}
                    title="Contact support"
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#0065FF';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 82, 204, 0.55)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#0052CC';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 82, 204, 0.45)';
                    }}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        <line x1="9" y1="10" x2="9" y2="10" strokeWidth="3" strokeLinecap="round" />
                        <line x1="12" y1="10" x2="12" y2="10" strokeWidth="3" strokeLinecap="round" />
                        <line x1="15" y1="10" x2="15" y2="10" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                    Support
                </button>
            )}
            <NewInquiry
                show={showForm}
                item={item}
                fromSupport
                isGuest={isGuest}
                onHide={() => setShowForm(false)}
                onNext={onSaveInquiry}
            />
            <NewInquiryComplete
                show={showComplete}
                item={item}
                isGuest={isGuest}
                onHide={() => setShowComplete(false)}
                onNext={onViewDetail}
            />
        </>
    );
}

export default SupportButton;
